import { yupResolver } from '@hookform/resolvers/yup'
import { Alert, Box, Stack, TextField } from '@mui/material'
import { ContainerLengthBayMappingDto } from '@yard/app/api/dg-config-api'
import { FormProps } from '@yard/app/models/form-props.model'
import { useTranslate } from '@tolgee/react'
import { Controller, Resolver, useForm } from 'react-hook-form'
import { mapDtoToFormProfile } from './container-length-bay-mapping-form.mapper'
import {
  ContainerLengthBayMappingFormProfile,
  ContainerLengthBayMappingSubmitProfile,
} from './container-length-bay-mapping-form.profile'
import { schema } from './container-length-bay-mapping-form.scheme'

interface Props extends FormProps {
  onSubmit: (formValues: ContainerLengthBayMappingSubmitProfile) => Promise<void> | void
  mapping?: ContainerLengthBayMappingDto
  existingLengths: number[]
}

const ContainerLengthBayMappingForm = ({ id, onSubmit, mapping, existingLengths }: Props) => {
  const { t } = useTranslate()
  const currentLength = mapping?.length
  const resolver = yupResolver(
    schema(existingLengths, currentLength),
  ) as Resolver<ContainerLengthBayMappingFormProfile, unknown, ContainerLengthBayMappingSubmitProfile>
  const { handleSubmit, control, formState } = useForm<
    ContainerLengthBayMappingFormProfile,
    unknown,
    ContainerLengthBayMappingSubmitProfile
  >({
    resolver,
    defaultValues: mapDtoToFormProfile(mapping),
  })

  return (
    <Box
      id={id}
      component='form'
      noValidate
      autoComplete='off'
      onSubmit={handleSubmit(onSubmit)}
      my={1}
    >
      <Stack gap={2}>
        {mapping?.isStandard && (
          <Alert severity='info'>
            {t(
              'containerLengthBayMappingStandardRowHint',
              'This is a standard length. Its length is locked, but the bay count can be adjusted for future allocations.',
            )}
          </Alert>
        )}
        <Controller
          name='length'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              fullWidth
              required
              disabled={mapping?.isStandard}
              type='number'
              label={t('containerLength', 'Container length')}
              error={!!formState.errors.length}
              helperText={formState.errors.length?.message}
              inputProps={{ min: 1, step: 1 }}
            />
          )}
        />
        <Controller
          name='bayCount'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              fullWidth
              required
              type='number'
              label={t('bayCount', 'Bay count')}
              error={!!formState.errors.bayCount}
              helperText={formState.errors.bayCount?.message}
              inputProps={{ min: 1, step: 1 }}
            />
          )}
        />
      </Stack>
    </Box>
  )
}

export default ContainerLengthBayMappingForm
